import type { Icon } from "@judo/model-api";
import type { SxProps, Theme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { resolveIconName } from "../utils/mdi-alias-map";

export interface IconRendererProps {
	/** Icon model object containing iconName, or a string icon name */
	icon: Icon | string | undefined | null;
	/** Additional CSS classes (e.g., 'mdi-spin', 'mdi-rotate-90') */
	className?: string;
	/** Icon size - maps to font-size */
	size?: "small" | "medium" | "large" | number;
	/** Icon color - inherits from parent by default */
	color?: string;
	/** MUI sx prop for additional styling */
	sx?: SxProps<Theme>;
}

const SIZE_MAP = {
	small: "1rem",
	medium: "1.5rem",
	large: "2rem",
} as const;

/**
 * Renders icons using MDI webfont classes.
 *
 * The icon is rendered as a <span> with MDI CSS classes:
 * - `mdi` - base class required for all icons
 * - `mdi-{iconName}` - specific icon (e.g., `mdi-home`, `mdi-account`)
 *
 * Icon name aliases are automatically resolved to canonical names using
 * the MDI metadata (e.g., `note-add` -> `note-plus`).
 *
 * Helper classes can be added via className prop:
 * - Rotation: `mdi-rotate-45`, `mdi-rotate-90`, `mdi-rotate-180`, etc.
 * - Flip: `mdi-flip-h`, `mdi-flip-v`
 * - Spin: `mdi-spin` (for loading indicators)
 *
 * @example
 * // Using Icon object from model
 * <IconRenderer icon={{ iconName: 'home' }} />
 *
 * // Using string directly
 * <IconRenderer icon="account" />
 *
 * // Using alias - automatically resolved
 * <IconRenderer icon="note-add" /> // renders as mdi-note-plus
 *
 * // With helper classes
 * <IconRenderer icon="loading" className="mdi-spin" />
 */
export function IconRenderer({ icon, className, size = "medium", color, sx: sxProp }: IconRendererProps) {
	if (!icon) {
		return null;
	}

	const rawIconName = typeof icon === "string" ? icon : (icon.iconName ?? icon.name);

	if (!rawIconName) {
		return null;
	}

	// Resolve alias to canonical name (e.g., 'note-add' -> 'note-plus')
	const iconName = resolveIconName(rawIconName);

	const fontSize = typeof size === "number" ? `${size}px` : SIZE_MAP[size];
	const iconClass = `mdi mdi-${iconName}${className ? ` ${className}` : ""}`;

	return (
		<Box
			component="span"
			className={iconClass}
			sx={{
				fontSize,
				color: color ?? "inherit",
				lineHeight: 1,
				verticalAlign: "middle",
				...((sxProp ?? {}) as Record<string, unknown>),
			}}
			aria-hidden="true"
			data-testid={`icon::${rawIconName}`}
		/>
	);
}
